import type { Spec, View } from "vega";

interface SceneNode {
  marktype?: string;
  role?: string;
  items?: SceneNode[];
}

/** The items a group of scene nodes drew, summed. */
function sumItems(nodes: SceneNode[] | undefined): number {
  return (nodes ?? []).reduce((count, node) => count + markItemCount(node), 0);
}

/** A mark's drawn items: its own shapes, plus those in the groups it holds. */
function markItems(mark: SceneNode): number {
  const own = mark.role === "mark" ? (mark.items?.length ?? 0) : 0;
  const scoped = mark.role === "mark" || mark.role === "scope";
  return own + (scoped ? sumItems(mark.items) : 0);
}

/** One child's drawn items: a mark's, none for a guide, else what it holds. */
function childItems(child: SceneNode): number {
  if (child.marktype !== undefined) return markItems(child);
  if (child.role === "axis" || child.role === "legend") return 0;
  return markItemCount(child);
}

/** How many items the spec's own marks drew — guides (axes, legends, titles) excluded. */
function markItemCount(node: SceneNode): number {
  return (node.items ?? []).reduce(
    (count, child) => count + childItems(child),
    0,
  );
}

/**
 * Names a chart that rendered without error yet drew nothing from rows that
 * exist — a filter or transform that removes every row, a mark reading a
 * data source nothing fills. Such a chart is broken, not empty: it shows only
 * its axes or legend. Null when it drew marks, or had no rows to draw.
 */
export function findEmptyDraw(
  view: View,
  spec: Spec,
  rowCount: number,
): string | null {
  if (rowCount === 0) return null;
  const root = (view.scenegraph() as unknown as { root: SceneNode }).root;
  if (markItemCount(root) > 0) return null;
  const sources = (spec.data ?? [])
    .map((data) => `"${data.name}": ${view.data(data.name).length} rows`)
    .join(", ");
  return `The chart draws no marks from ${rowCount} rows — a filter or transform removes every row before any mark reads it (data after transforms: ${sources}).`;
}
